import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { FormBase } from 'src/app/_models/form-base';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ModalService } from 'src/app/_services/modal.service';
import { ErrorObject } from 'src/app/_models/error';
import { User, UserUpdate } from 'src/app/_models/user';
import { UserService } from 'src/app/_services/user.service';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { MustMatch } from 'src/app/_helpers/must-match.validator';
import { Subject, takeUntil } from 'rxjs';
import {
  passwordRegex,
  passesLengthValidation,
  passesNumberValidation,
  passesSpecialCharValidation,
  passesUpperCaseValidation,
  passesLowerCaseValidation,
} from 'src/app/_helpers/utils';
import {
  UserDeleteResponse,
  UserUpdateResponse,
} from 'src/app/_models/api-responses';
import { Role, SUPER_ADMIN_ROLE_LIST } from 'src/app/_models/role';
import { Router } from '@angular/router';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { ValidationModalComponent } from '../../_components/modals/validation-modal/validation-modal.component';
import { ModalComponent } from '../../_components/modals/modal.component';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { UnderlinedFormFieldComponent } from '../../_components/forms/underlined-form-field/underlined-form-field.component';
import { ButtonCardComponent } from '../../_components/cards/button-card/button-card.component';
import { ValidationRowComponent } from 'src/app/_components/validation-row/validation-row.component';
import { LabCardComponent } from 'src/app/_components/cards/lab-card/lab-card.component';
import { Laboratory } from 'src/app/_models/laboratory';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RecoveryCodesModalComponent } from '../../_components/modals/recovery-codes-modal/recovery-codes-modal.component';
import { TwoFactorStatus } from 'src/app/_models/2fa';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
  imports: [
    ButtonCardComponent,
    FormsModule,
    ReactiveFormsModule,
    UnderlinedFormFieldComponent,
    ButtonComponent,
    ModalComponent,
    ValidationModalComponent,
    RoleCheckPipe,
    ValidationRowComponent,
    LabCardComponent,
    MatIcon,
    MatSlideToggleModule,
    RecoveryCodesModalComponent,
    LoadingSpinnerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private readonly userService = inject(UserService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);

  private readonly unsubscribe$ = new Subject<void>();
  private readonly allowNavigation = new Subject<boolean>();

  currentUser: User;
  laboratory: Laboratory;
  twoFactorUserSettings = signal<TwoFactorStatus | null>(null);

  readonly Role = Role;
  readonly superAdminRoles = SUPER_ADMIN_ROLE_LIST;

  isLoading = signal(true);
  error = signal<ErrorObject | null>(null);
  updateIsProcessing = signal(false);
  emailIsProcessing = signal(false);
  passwordIsProcessing = signal(false);
  generateRecoveryCodesProcessing = signal(false);
  updateUserSuccessMessage = signal<string | null>(null);
  showRecoveryCodes = signal(false);
  recoveryCodes = signal<string[] | null>(null);

  firstNameField = new FormBase({
    key: 'firstName',
    label: 'First Name',
    type: 'text',
    placeholder: 'First name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  lastNameField = new FormBase({
    key: 'lastName',
    label: 'Last Name',
    type: 'text',
    placeholder: 'Last name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  newEmailAddressField = new FormBase({
    key: 'newEmail',
    label: 'New Email Address',
    type: 'email',
    placeholder: 'New email address',
    disabled: false,
    required: true,
    value: '',
    options: [],
    autocomplete: 'email',
  });

  confirmNewEmailAddressField = new FormBase({
    key: 'confirmNewEmail',
    label: 'Confirm New Email Address',
    type: 'email',
    placeholder: 'Confirm new email address',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  passwordField = new FormBase({
    key: 'password',
    label: 'Current Password',
    type: 'password',
    placeholder: 'Password',
    disabled: false,
    required: true,
    value: '',
    options: [],
    autocomplete: 'current-password',
  });

  newPasswordField = new FormBase({
    key: 'newPassword',
    label: 'New Password',
    type: 'password',
    placeholder: 'New password',
    disabled: false,
    required: true,
    value: '',
    options: [],
    autocomplete: 'new-password',
  });

  confirmNewPasswordField = new FormBase({
    key: 'confirmNewPassword',
    label: 'Confirm New Password',
    type: 'password',
    placeholder: 'Confirm new password',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  recoveryCodesPasswordField = new FormBase({
    key: 'recoveryCodesPassword',
    label: 'Current Password',
    type: 'password',
    placeholder: 'Enter your password',
    disabled: false,
    required: true,
    value: '',
    options: [],
    autocomplete: 'current-password',
  });

  recoveryCodesCodeField = new FormBase({
    key: 'recoveryCodesCode',
    label: 'Authentication Code',
    type: 'text',
    placeholder: 'Enter 6-digit code',
    disabled: false,
    required: true,
    value: '',
    options: [],
    autocomplete: 'one-time-code',
  });

  updateDetailsForm = this.formBuilder.group({
    [this.firstNameField.key]: [
      '',
      [Validators.minLength(1), Validators.maxLength(100)],
    ],
    [this.lastNameField.key]: [
      '',
      [Validators.minLength(1), Validators.maxLength(100)],
    ],
  });

  updateEmailForm = this.formBuilder.group(
    {
      [this.newEmailAddressField.key]: [
        '',
        [Validators.required, Validators.email],
      ],
      [this.confirmNewEmailAddressField.key]: [
        '',
        [Validators.required, Validators.email],
      ],
    },
    {
      validators: MustMatch(
        this.newEmailAddressField.key,
        this.confirmNewEmailAddressField.key,
      ),
    },
  );

  updatePasswordForm = this.formBuilder.group(
    {
      [this.passwordField.key]: ['', [Validators.required]],
      [this.newPasswordField.key]: [
        '',
        [Validators.required, Validators.pattern(passwordRegex)],
      ],
      [this.confirmNewPasswordField.key]: [
        '',
        [Validators.required, Validators.pattern(passwordRegex)],
      ],
    },
    {
      validators: MustMatch(
        this.newPasswordField.key,
        this.confirmNewPasswordField.key,
      ),
    },
  );

  recoveryCodesForm = this.formBuilder.group({
    [this.recoveryCodesPasswordField.key]: ['', [Validators.required]],
    [this.recoveryCodesCodeField.key]: [
      '',
      [Validators.required, Validators.pattern(/^\d{6}$/)],
    ],
  });

  ngOnInit(): void {
    this.authenticationService.currentUser$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user: User) => {
        this.currentUser = user;
      });

    this.laboratory = this.authenticationService.selectedLaboratory;
    this.getUser();
  }

  passesValidation(validationType: string): boolean {
    switch (validationType) {
      case 'length':
        return passesLengthValidation(this.pf.newPassword.value as string);
      case 'uppercaseLetter':
        return passesUpperCaseValidation(this.pf.newPassword.value as string);
      case 'lowercaseLetter':
        return passesLowerCaseValidation(this.pf.newPassword.value as string);
      case 'number':
        return passesNumberValidation(this.pf.newPassword.value as string);
      case 'specialCharacter':
        return passesSpecialCharValidation(this.pf.newPassword.value as string);
      default:
        return false;
    }
  }

  getUser(): void {
    this.userService
      .getUserDetails(this.currentUser.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (data: User) => {
          this.currentUser = data;
          this.updateFormValues(this.currentUser);
          this.get2FASettings();
        },
        error: (err: ErrorObject) => {
          this.isLoading.set(false);
          this.error.set(err);
          this.openModal('error');
        },
      });
  } 

  get2FASettings(): void {
    this.userService
      .getTwoFactorStatus()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (data: TwoFactorStatus) => {
          this.twoFactorUserSettings.set(data);
          this.isLoading.set(false);
        },
        error: (err: ErrorObject) => {
          this.isLoading.set(false);
          this.error.set(err);
          this.openModal('error');
        },
      });
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.close(modalId);
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'close-button':
        this.closeModal('success');
        this.closeModal('update-email');
        this.closeModal('error');
        break;
      case 'cancel-button':
        this.allowNavigation.next(false);
        this.closeModal('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.closeModal('attention');
        break;
      case 'logout':
        void this.router.navigate(['/login']);
        break;
    }
  }

  get f(): FormGroup['controls'] {
    return this.updateDetailsForm.controls;
  }

  get ef(): FormGroup['controls'] {
    return this.updateEmailForm.controls;
  }

  get pf(): FormGroup['controls'] {
    return this.updatePasswordForm.controls;
  }

  updateFormValues(userDetails: User): void {
    this.updateDetailsForm.reset();
    this.updateDetailsForm.patchValue({
      [this.firstNameField.key]: userDetails.firstName,
      [this.lastNameField.key]: userDetails.lastName,
    });
    this.updateDetailsForm.markAsPristine();
  }

  onSubmit(): void {
    if (this.updateIsProcessing() || this.passwordIsProcessing()) return;

    this.updateIsProcessing.set(true);
    this.error.set(null);
    this.updateUserSuccessMessage.set(null);

    const userUpdate: UserUpdate = { id: this.currentUser.id };
    if (this.f.firstName.value !== this.currentUser.firstName) {
      userUpdate.firstName = this.f.firstName.value as string;
    }
    if (this.f.lastName.value !== this.currentUser.lastName) {
      userUpdate.lastName = this.f.lastName.value as string;
    }

    this.userService
      .updateUser(userUpdate)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (data: UserUpdateResponse) => {
          this.updateIsProcessing.set(false);
          this.updateUserSuccessMessage.set(data.message);
          this.currentUser.firstName = data.user.firstName;
          this.currentUser.lastName = data.user.lastName;
          this.updateDetailsForm.markAsPristine();
          this.updateUserSuccessMessage.set(data.message);
          this.openModal('success');
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.updateIsProcessing.set(false);
          this.openModal('error');
        },
      });
  }

  onSubmitEmail(): void {
    if (
      this.emailIsProcessing() ||
      this.updateIsProcessing() ||
      this.passwordIsProcessing()
    )
      return;

    this.emailIsProcessing.set(true);
    this.error.set(null);

    this.userService
      .updateEmail(this.currentUser.id, this.ef.newEmail.value as string)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.emailIsProcessing.set(false);
          this.updateEmailForm.reset();
          this.updateEmailForm.markAsPristine();
          this.openModal('update-email');
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.emailIsProcessing.set(false);
          this.openModal('error');
        },
      });
  }

  onSubmitPassword(): void {
    if (this.passwordIsProcessing() || this.updateIsProcessing()) return;

    this.passwordIsProcessing.set(true);
    this.error.set(null);
    this.updateUserSuccessMessage.set(null);

    this.userService
      .updatePassword(
        this.currentUser.id,
        this.pf.password.value as string,
        this.pf.newPassword.value as string,
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.passwordIsProcessing.set(false);
          this.updatePasswordForm.reset();
          this.updatePasswordForm.markAsPristine();
          this.updateUserSuccessMessage.set('Your password has been updated.');
          this.openModal('success');
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.passwordIsProcessing.set(false);
          this.openModal('error');
        },
      });
  }

  generateRecoveryCodes(): void {
    this.showRecoveryCodes.set(false);
    this.recoveryCodes.set([]);
    this.recoveryCodesForm.reset();
    this.error.set(null);
    this.openModal('recovery-codes');
  }

  onRecoveryCodesModalButtonClicked(buttonId: string): void {
    if (buttonId === 'generate-btn') {
      this.generateRecoveryCodesProcessing.set(true);
      this.error.set(null);
      const password = this.recoveryCodesForm.value[
        this.recoveryCodesPasswordField.key
      ] as string;
      const code = this.recoveryCodesForm.value[
        this.recoveryCodesCodeField.key
      ] as string;
      this.userService
        .generateRecoveryCodes(password, code)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (response) => {
            this.generateRecoveryCodesProcessing.set(false);
            this.recoveryCodes.set(response.recoveryCodes);
            this.showRecoveryCodes.set(true);
            this.authenticationService.patchCurrentUser({
              recoveryCodesRemaining: response.recoveryCodes.length,
            });
          },
          error: (err: ErrorObject) => {
            this.error.set(err);
            this.generateRecoveryCodesProcessing.set(false);
          },
        });
    } else {
      this.closeModal('recovery-codes');
    }
  }

  signOutOtherDevices(): void {
    this.error.set(null);
    this.userService
      .signOutOtherDevices()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.updateUserSuccessMessage.set(
            'You have been signed out of all other devices.',
          );
          this.openModal('success');
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.openModal('error');
        },
      });
  }

  deleteAccount(): void {
    this.error.set(null);
    this.userService
      .deleteUser(this.currentUser.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (data: UserDeleteResponse) => {
          this.closeModal('delete-account');
          if (data.logout) {
            void this.authenticationService.logout();
            this.openModal('logout');
            return;
          }
          this.updateUserSuccessMessage.set(data.message);
          this.openModal('success');
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.closeModal('delete-account');
          this.openModal('error');
        },
      });
  }

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private isNavigationAllowed(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (
        (this.updateDetailsForm.pristine &&
          this.updateEmailForm.pristine &&
          this.updatePasswordForm.pristine) ||
        !this.currentUser
      ) {
        resolve(true);
      } else {
        this.openModal('attention');
        this.allowNavigation.subscribe((isConfirmed) => resolve(isConfirmed));
      }
    });
  }
}
